import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';
import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('component.kiera.assistant.widget');
import { OnDestroy, OnInit } from '@angular/core';
import { Service } from 'src/libs/portal/season/service';

const ASSISTANT_RUNTIME_API_KEY_STORAGE = 'kiera.assistant.runtime_api_key';
const assistantApi = new Wiz('/wiz').app('page.kiera.assistant');

type FloatingChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    createdAt: number;
};

@Component({
 standalone: false,
 selector: 'wiz-component-kiera-assistant-widget',
template: templateSource || '',
    styles: [``],
})
export class ComponentKieraAssistantWidgetComponent implements OnInit, OnDestroy {
    public open: boolean = false;
    public settingsOpen: boolean = false;
    public config: any = {
        endpoint: '',
        model: '',
        api_key: '',
        system_prompt: '',
        temperature: 0.2,
    };
    public draft: string = '';
    public messages: FloatingChatMessage[] = [];
    public sending: boolean = false;
    public saving: boolean = false;
    public errorMessage: string = '';
    public errorDetail: string = '';
    public saveMessage: string = '';

    private configLoaded: boolean = false;
    private configLoading: Promise<void> | null = null;

    private readonly storageSyncHandler = (event: StorageEvent) => {
        if (event.key !== ASSISTANT_RUNTIME_API_KEY_STORAGE) {
            return;
        }
        const nextApiKey = this.readRuntimeApiKey();
        if (String(this.config.api_key || '') === nextApiKey) {
            return;
        }
        this.config = {
            ...this.config,
            api_key: nextApiKey,
        };
        void this.service.render();
    };

    constructor(@Inject( Service) public service: Service) { }

    private readRuntimeApiKey() {
        try {
            const localValue = globalThis.localStorage?.getItem(ASSISTANT_RUNTIME_API_KEY_STORAGE) || '';
            if (localValue !== '') {
                return localValue;
            }
            const legacySessionValue = globalThis.sessionStorage?.getItem(ASSISTANT_RUNTIME_API_KEY_STORAGE) || '';
            if (legacySessionValue !== '') {
                globalThis.localStorage?.setItem(ASSISTANT_RUNTIME_API_KEY_STORAGE, legacySessionValue);
                globalThis.sessionStorage?.removeItem(ASSISTANT_RUNTIME_API_KEY_STORAGE);
            }
            return legacySessionValue;
        } catch (error) {
            return '';
        }
    }

    private writeRuntimeApiKey(value: string) {
        try {
            const text = String(value || '');
            globalThis.sessionStorage?.removeItem(ASSISTANT_RUNTIME_API_KEY_STORAGE);
            if (text === '') {
                globalThis.localStorage?.removeItem(ASSISTANT_RUNTIME_API_KEY_STORAGE);
            } else {
                globalThis.localStorage?.setItem(ASSISTANT_RUNTIME_API_KEY_STORAGE, text);
            }
        } catch (error) {
            return;
        }
    }

    private clearMessagesState() {
        this.messages = [];
        this.draft = '';
        this.errorMessage = '';
        this.errorDetail = '';
        this.saveMessage = '';
    }

    private applyConfigData(data: any) {
        this.config = {
            endpoint: data.endpoint || '',
            model: data.model || '',
            api_key: this.readRuntimeApiKey(),
            system_prompt: data.system_prompt || '',
            temperature: typeof data.temperature === 'number' ? data.temperature : 0.2,
        };
    }

    public async ngOnInit() {
        await this.service.init();
        try {
            globalThis.addEventListener?.('storage', this.storageSyncHandler);
        } catch (error) {
            return;
        }
        await this.service.render();
    }

    public ngOnDestroy() {
        try {
            globalThis.removeEventListener?.('storage', this.storageSyncHandler);
        } catch (error) {
            return;
        }
    }

    public hasSession() {
        return !!(this.service.auth.session?.id || this.service.auth.session?.email);
    }

    public isSuppressedRoute() {
        return String(globalThis.location?.pathname || '').indexOf('/kiera/sol/') === 0;
    }

    public async loadConfig(render: boolean = true, force: boolean = false) {
        if (!force && this.configLoaded) {
            if (render) {
                await this.service.render();
            }
            return;
        }
        if (this.configLoading) {
            await this.configLoading;
            if (render) {
                await this.service.render();
            }
            return;
        }

        this.configLoading = this.fetchConfig();
        try {
            await this.configLoading;
        } finally {
            this.configLoading = null;
        }

        if (render) {
            await this.service.render();
        }
    }

    private async fetchConfig() {
        const { code, data } = await assistantApi.call('config');
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to load assistant settings.';
            return;
        }

        this.applyConfigData(data || {});
        this.configLoaded = true;
    }

    public async toggleOpen(force?: boolean) {
        const next = typeof force === 'boolean' ? force : !this.open;
        if (next) {
            this.open = true;
            this.settingsOpen = false;
            await this.loadConfig(false);
        } else {
            this.open = false;
            this.settingsOpen = false;
            this.clearMessagesState();
        }
        await this.service.render();
    }

    public async openSettings(event?: Event) {
        event?.preventDefault();
        event?.stopPropagation();
        if (!this.open) {
            this.open = true;
        }
        this.errorMessage = '';
        this.errorDetail = '';
        this.saveMessage = '';
        this.settingsOpen = true;
        await this.service.render();
        await this.loadConfig();
    }

    public async closeSettings() {
        this.settingsOpen = false;
        await this.service.render();
    }

    public async saveConfig() {
        this.errorMessage = '';
        this.errorDetail = '';
        this.saveMessage = '';
        this.writeRuntimeApiKey(this.config.api_key || '');
        this.saving = true;
        await this.service.render();

        const { code, data } = await assistantApi.call('save_config', {
            endpoint: this.config.endpoint || '',
            model: this.config.model || '',
            system_prompt: this.config.system_prompt || '',
            temperature: String(this.config.temperature ?? 0.2),
        });

        this.saving = false;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to save assistant settings.';
            await this.service.render();
            return;
        }

        this.applyConfigData(data || {});
        this.configLoaded = true;
        this.saveMessage = 'Assistant settings saved.';
        this.settingsOpen = false;
        await this.service.render();
    }

    public async clearConversation() {
        this.clearMessagesState();
        await this.service.render();
    }

    public async onDraftKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            await this.send();
        }
    }

    public async send() {
        const text = (this.draft || '').trim();
        if (text === '' || this.sending) {
            return;
        }

        const history = this.messages.slice(-12).map((message) => ({ role: message.role, content: message.content }));
        this.messages = [
            ...this.messages,
            { role: 'user', content: text, createdAt: Date.now() },
        ];
        this.draft = '';
        this.errorMessage = '';
        this.errorDetail = '';
        this.saveMessage = '';
        this.sending = true;
        await this.service.render();

        const { code, data } = await assistantApi.call('request', {
            query: text,
            history: JSON.stringify(history),
            api_key: this.config.api_key || '',
            source: 'floating-widget',
            page_path: globalThis.location?.pathname || '',
        });

        this.sending = false;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Assistant request failed.';
            this.errorDetail = data?.detail || '';
            await this.service.render();
            return;
        }

        this.messages = [
            ...this.messages,
            {
                role: 'assistant',
                content: data?.reply || 'No response received.',
                createdAt: Date.now(),
            },
        ];
        await this.service.render();
    }

    public isConfigReady() {
        return !!((this.config.endpoint || '').trim() && (this.config.model || '').trim());
    }
}

export default ComponentKieraAssistantWidgetComponent;
